import React, { useState, useEffect } from "react";
import styles from "./workshopCard.module.css";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import dummy from "../../Assets/dummy.png";
import axios from "axios";
import {
  open_api_without_auth,
  open_api_with_cookies,
  getCountryCode,
} from "../../Utils/network";

function WorkshopCard({ data, is_workshop_page = false }) {
  const [expertsData, setExpertsData] = useState([]);
  const [expertsLoading, setExpertsLoading] = useState(false);
  const [countryCode, setCountryCode] = useState("in");
  const navigate = useNavigate();

  const handleBuyNow = (workshopSlug) => {
    navigate(`/workshop/${workshopSlug}`);
  };

  useEffect(() => {
    const fetchCountry = async () => {
      const countryCode = await getCountryCode();
      setCountryCode(countryCode);
    };
    fetchCountry();
  }, []);

  // Fetch expert details
  useEffect(() => {
    const fetchExpertDetails = async () => {
      if (!data?.workshop_experts || data.workshop_experts.length === 0) return;

      setExpertsLoading(true);
      try {
        const expertPromises = data.workshop_experts.map((expertId) =>
          open_api_without_auth.get(`/profiles/expert/${expertId}`)
        );

        const results = await Promise.all(expertPromises);
        const expertDetails = results.map((res) => res.data);
        setExpertsData(expertDetails);
      } catch (error) {
        console.error("Error fetching expert details:", error);
      } finally {
        setExpertsLoading(false);
      }
    };
    if (!is_workshop_page) {
      fetchExpertDetails();
    } else {
      setExpertsData(data?.workshop_experts);
    }
  }, [data?.workshop_experts, is_workshop_page]);

  // Format start date
  const formatStartDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "long" });
    return `${day}${getDaySuffix(day)} ${month}`;
  };

  // Get day suffix (st, nd, rd, th)
  const getDaySuffix = (day) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  // Calculate discount percentage
  const calculateDiscount = () => {
    const workshopMrp = countryCode === "in" ? data?.mrp : data?.mrp_usd;
    const workshopSellingPrice =
      countryCode === "in" ? data?.selling_price : data?.selling_price_usd;
    if (!workshopMrp || !workshopSellingPrice) return 0;
    return Math.round(
      ((workshopMrp - workshopSellingPrice) / workshopMrp) * 100
    );
  };

  // Get expert names combined
  const getExpertName = () => {
    if (expertsLoading) return "";
    if (!expertsData || expertsData.length === 0) return "";

    if (expertsData.length === 1) {
      const expert = expertsData[0];
      // Extract name from the expert data, accounting for potentially different API response structure
      const firstName = expert.first_name || expert.user?.first_name || "";
      const lastName = expert.last_name || expert.user?.last_name || "";
      return `by ${firstName} ${lastName}`.trim();
    } else {
      const names = expertsData.map((expert) => {
        const firstName = expert.first_name || expert.user?.first_name || "";
        const lastName = expert.last_name || expert.user?.last_name || "";
        return `${firstName} ${lastName}`.trim();
      });

      // For multiple names, join with commas and 'and' for the last one
      const lastExpert = names.pop();
      return `by ${names.join(", ")} and ${lastExpert}`;
    }
  };

  const discountPercentage = calculateDiscount();
  const startDate = formatStartDate(data?.start_date_time);
  const expertName = getExpertName();

  // Check if data exists
  if (!data) {
    return null;
  }

  const priceSymbol = countryCode === "in" ? "₹" : "$";
  const workshopSellingPrice =
    countryCode === "in" ? data?.selling_price : data?.selling_price_usd;
  const workshopMrp = countryCode === "in" ? data?.mrp : data?.mrp_usd;

  // Render the workshop cards
  return (
    <div className={styles.card}>
      <div className={styles.imageContainer}>
        <img
          src={data?.file || dummy}
          alt={data?.title || "Workshop"}
          className={styles.cardImage}
        />

        {data?.type?.title === "Live Schedule" ? (
          <div className={styles.liveScheduleBadge}>Live Schedule</div>
        ) : (
          <div className={styles.onDemandBadge}>Recorded</div>
        )}
      </div>
      {startDate && data?.type?.title === "Live Schedule" && (
        <div className={styles.startDateBadge}>Starts {startDate}</div>
      )}

      <div className={styles.cardContent}>
        <div>
          <h2 className={styles.courseTitle}>{data?.title}</h2>
          <p className={styles.expertName}>{expertName}</p>
        </div>

        <div className={styles.priceContainer}>
          {data?.selling_price !== 0 && data?.selling_price_usd !== 0 && (
            <div className={styles.pricingSection}>
              <div className={styles.priceRow}>
                {discountPercentage > 0 && (
                  <span className={styles.discountBadge}>
                    -{discountPercentage}%
                  </span>
                )}
                <span className={styles.currentPrice}>
                  {priceSymbol} {workshopSellingPrice}
                </span>
              </div>
              {data?.mrp && (
                <span className={styles.originalPrice}>
                  M.R.P: {priceSymbol} {workshopMrp}
                </span>
              )}
            </div>
          )}

          <div className={styles.buttonContainer}>
            <Button
              variant="contained"
              className={styles.buyNowButton}
              onClick={() => handleBuyNow(data?.slug)}
            >
              {data?.selling_price !== 0 && data?.selling_price_usd !== 0
                ? "Buy now"
                : "Free"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WorkshopCard;
